import { Button, CloseButton } from '@ui/shared/Button';
import * as React from 'react';
import { combineLatest } from 'rxjs';
import styled from 'styled-components';
import { ReportSettings } from '../../../data/providers/reports/types';
import { ISettingsTemplate } from '../../../data/providers/settings/reports/types';
import {
  IUserHasTemplateMap,
  templateProvider,
} from '../../../data/providers/templatesProvider';
import { userProvider } from '../../../data/providers/userProvider';
import { coachesProvider, ICoach } from '../../../data/providers/usersProvider';
import { IUser } from '../../../data/types';
import { shareTemplate } from '../../../data/writers/shareTemplate';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { ScrollableArea } from '../../layout/ScrollableArea';
import { CardFooter } from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { SearchInput } from '../../shared/form/SearchInput';
import { SelectBox } from '../../shared/form/SelectBox';
import { List } from '../../shared/List/List';
import { ListHeader } from '../../shared/List/ListHeader';
import {
  ListItem,
  ListItemCell,
  ListItemSafeText,
} from '../../shared/List/ListItem';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { NoResults } from '../../shared/NoResults';
import { PopupCard, PopupCardContent } from '../../shared/popups/PopupCard';
import { AddUser } from './AddUser';
import { EditUser } from './edit/EditUser';

interface IProps {
  onClose: () => void;
  onSelect?: (userID: string, checked: boolean) => void;
  reportType?: string;
  reportTypeId?: string;
  selected?: boolean;
  template?: ISettingsTemplate<ReportSettings>;
  shareTemplate?: boolean;
}

export const ManageUsers = (props: IProps): React.ReactElement => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [users, setUsers] = React.useState<ICoach[]>([]);
  const [requestingUser, setRequestingUser] = React.useState<IUser>();
  const [search, setSearch] = React.useState('');
  const [usersToShareTemplate, setUsersToShareTemplate] = React.useState<
    string[]
  >([]);
  const [userTemplateStatus, setUserTemplateStatus] = React.useState({});

  /**
   * @description subscribe to users that logged in user has access to
   */
  React.useEffect((): (() => void) => {
    const subscriber = combineLatest([
      coachesProvider(),
      userProvider(),
    ]).subscribe((results: [ICoach[], IUser]): void => {
      const [mUsers, mRequestingUser] = results;
      setRequestingUser(mRequestingUser);
      setUsers(mUsers);
      setLoading(false);
    });
    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description subscribe to users that already have the incoming template to be shared
   */
  React.useEffect((): (() => void) => {
    if (props.template?.id && props.reportType && props.reportTypeId) {
      const subscriber = templateProvider(
        users,
        props.reportTypeId,
        props.template.id
      ).subscribe((results: IUserHasTemplateMap): void => {
        setUserTemplateStatus(results);
        setLoading(false);
      });
      return (): void => subscriber.unsubscribe();
    }
  }, [users]);

  /**
   * @description
   * @param value
   * @returns
   */
  const searchUpdateHandler = (value: string): void => setSearch(value);

  /**
   * @description finds users that match the search filter by name or email
   * @param mCoaches
   * @param mSearch
   * @returns
   */
  const runSearch = (mCoaches: ICoach[], mSearch: string): ICoach[] => {
    if (mSearch) {
      return mCoaches.filter((item: ICoach): boolean => {
        return (
          item.name.toLowerCase().includes(mSearch.toLowerCase()) ||
          item.email.toLowerCase().includes(mSearch.toLowerCase())
        );
      }) as ICoach[];
    } else {
      return mCoaches;
    }
  };

  /**
   * @description clears search
   * @returns
   */
  const clearSearch = (): void => setSearch('');

  /**
   * @description writes shared template to selected users
   * @param userIds {string} array of user IDs to share template with
   * @param template {ITemplate} template to be shared
   */
  const handleSubmit = async (
    userIds: string[],
    template: ISettingsTemplate<ReportSettings>,
    reportType: string
  ): Promise<void> => {
    userIds.forEach((user: string): void => {
      shareTemplate(reportType, template, user);
    });
  };

  /**
   * @description memoized list of users that runs search when search or user list changes
   */
  const data = React.useMemo((): ICoach[] => {
    return runSearch(users, search);
  }, [JSON.stringify(users), search]);

  const renderSelectBox = (userId: string): React.ReactElement => {
    const doesUserHaveTemplate = userTemplateStatus[userId];
    return doesUserHaveTemplate ? (
      <ListItemCell>Already Shared</ListItemCell>
    ) : (
      <SelectBoxItem
        userID={userId}
        onSelect={handleUserSelect}
        selected={usersToShareTemplate.includes(userId)}
      />
    );
  };

  const handleUserSelect = (userID: string): void => {
    const usersToShareWith = usersToShareTemplate.includes(userID)
      ? usersToShareTemplate.filter((id: string): boolean => id !== userID)
      : usersToShareTemplate.concat([userID]);
    setUsersToShareTemplate(usersToShareWith);
  };

  /**
   * @description renders list rows
   * @param param0
   */
  const rowRenderer = ({
    index,
    key,
    parent,
    style,
  }: {
    index: number;
    key: string;
    parent: unknown;
    style: React.CSSProperties;
  }): React.ReactElement => {
    const row = data[index];

    return (
      <ListItem key={key} style={style}>
        <ListItemCell>
          <ListItemSafeText>{row.name}</ListItemSafeText>
        </ListItemCell>
        <ListItemCell>
          <ListItemSafeText>{row.email}</ListItemSafeText>
        </ListItemCell>
        <ListItemCell>
          <ListItemSafeText>
            <h5 style={{ color: 'var(--color-accent)', marginRight: '15px' }}>
              <b>{row.teamCount}</b>
            </h5>
          </ListItemSafeText>
        </ListItemCell>

        {props.shareTemplate ? (
          renderSelectBox(row.id)
        ) : (
          <ListItemCell>
            <EditUser user={row} requestingUser={requestingUser} />
          </ListItemCell>
        )}
      </ListItem>
    );
  };

  return (
    <PopupCard size='large' onClose={props.onClose} noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8} borderless>
          <CardHeaderLeft>
            {props.shareTemplate ? (
              <h5>{`Share Template: ${props.reportType} - ${props.template.name}`}</h5>
            ) : (
              <h5>Manage Users</h5>
            )}
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <ScrollableArea
          style={{ backgroundColor: 'var(--color-list-background)' }}
        >
          <CardHeader withListHeader notRounded surface={8}>
            <Column style={{ paddingRight: '10px' }}>
              <Row>
                <CardHeaderLeft>
                  <SearchInput
                    value={search}
                    onChange={searchUpdateHandler}
                    onClear={clearSearch}
                  />
                </CardHeaderLeft>
                {props.shareTemplate ? null : (
                  <CardHeaderRight>
                    <AddUser />
                  </CardHeaderRight>
                )}
              </Row>
              {props.shareTemplate ? (
                <ListHeader
                  labels={['Name', 'Email', 'Teams', 'Share Template?']}
                />
              ) : (
                <ListHeader labels={['Name', 'Email', 'Teams', 'Edit']} />
              )}
            </Column>
          </CardHeader>
          {loading ? (
            <LinearLoading />
          ) : data && data.length > 0 ? (
            <List
              listLength={data.length}
              rowRenderer={rowRenderer}
              rowHeight={80}
            />
          ) : (
            <NoResults
              message='No Users Found'
              clearFilter={clearSearch}
              filterApplied={search.length > 0}
            />
          )}
        </ScrollableArea>
        <CardFooter>
          {props.shareTemplate && (
            <CardHeaderRight>
              <Button
                title='Share Template'
                onClick={(): Promise<void> =>
                  handleSubmit(
                    usersToShareTemplate,
                    props.template,
                    props.reportTypeId
                  ).then((): void => props.onClose())
                }
              />
            </CardHeaderRight>
          )}
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

interface ISelectBoxProps {
  selected: boolean;
  onSelect: (userID: string) => void;
  userID: string;
}

const SelectBoxItem = (props: ISelectBoxProps): React.ReactElement => {
  const selectionHandler = (
    e: React.SyntheticEvent<HTMLInputElement>
  ): void => {
    props.onSelect(props.userID);
  };

  return (
    <ListItemCell>
      <SelectBox selected={props.selected} onSelect={selectionHandler} />
    </ListItemCell>
  );
};

const PermissionsList = styled.ul`
  display: flex;
  flex-direction: column;
  justify-content: center;
  list-style-type: none;
  padding: 5px;
`;

const PermissionItem = styled.li``;

const TeamText = styled.p`
  display: flex;
  align-items: center;
`;
